/**
 * TenantStateNotice — the explanatory states for a tenant that cannot show its
 * normal surfaces yet (tenant-foundation Task 18 / Req 15.4, 15.5, 15.7).
 *
 * WHY THIS EXISTS. Two tenant-level conditions previously rendered as an app that
 * looked broken rather than one that explained itself:
 *
 *   - SUSPENDED — a platform operator puts a whole organization on hold. Before
 *     this, the only trace anywhere in the UI was a "(suspended)" suffix inside the
 *     tenant switcher's dropdown — and the switcher renders nothing at all for a
 *     single-membership user, so precisely the people most affected (everyone in a
 *     one-tenant org) saw no explanation whatsoever, just surfaces that quietly
 *     refused to load.
 *   - NEW / EMPTY — a freshly provisioned tenant has no roster, no events and no
 *     bulletins. Per-surface empty states each say "nothing here yet", which reads
 *     as breakage when EVERY surface says it at once. The tenant-level state says
 *     the useful thing instead: the organization is set up, it is just new.
 *
 * Built on the shared `EmptyState` primitive rather than a bespoke panel, so this
 * inherits the app's chrome, tokens and `role="status"` semantics — except for the
 * suspended variant, which is an `alert`: it reports a condition the viewer cannot
 * resolve themselves and needs to notice, not a quiet absence of data.
 *
 * Copy is exported as named constants so tests assert against one definition
 * (the convention set by `components/herald/EmptyState.tsx`).
 */

import type { JSX } from 'react';
import { PauseCircle, Sparkles } from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';

/** Heading for a tenant under an operator hold (Req 15.7). */
export const TENANT_SUSPENDED_HEADLINE = 'This organization is paused';
/**
 * Body for the suspended state. Deliberately says WHO can fix it and does NOT
 * speculate about why: the reason lives with the platform operator, and guessing
 * at it in the UI (billing? violation?) would be both wrong and alarming.
 */
export const TENANT_SUSPENDED_BODY =
  'A platform administrator has paused access for your organization. Your data is safe and nothing has been deleted. Please contact your platform administrator to restore access.';

/** Heading for a provisioned-but-empty tenant (Req 15.4, 15.5). */
export const TENANT_EMPTY_HEADLINE = 'Your organization is ready';
/** Body for the empty state — reassurance plus the one obvious next step. */
export const TENANT_EMPTY_BODY =
  'Everything is set up, there just isn’t any activity yet. Once people join and your season is set up, this is where it will show up.';

export type TenantNoticeKind = 'suspended' | 'empty';

export interface TenantStateNoticeProps {
  /** Which tenant-level condition to explain. */
  kind: TenantNoticeKind;
  /**
   * The tenant's display name, woven into the heading when known so the notice
   * reads as being about a real organization rather than a generic failure.
   */
  tenantName?: string;
  /** Optional action (e.g. an invite link for an empty tenant). */
  cta?: React.ReactNode;
  className?: string;
}

export function TenantStateNotice({
  kind,
  tenantName,
  cta,
  className,
}: TenantStateNoticeProps): JSX.Element {
  const suspended = kind === 'suspended';
  const headline = suspended
    ? tenantName
      ? `${tenantName} is paused`
      : TENANT_SUSPENDED_HEADLINE
    : tenantName
      ? `${tenantName} is ready`
      : TENANT_EMPTY_HEADLINE;

  return (
    <div
      data-testid="tenant-state-notice"
      data-kind={kind}
      // A suspended tenant is a condition the viewer must notice and cannot fix;
      // an empty tenant is an ordinary absence of data.
      role={suspended ? 'alert' : undefined}
    >
      <EmptyState
        icon={
          suspended ? (
            <PauseCircle className="h-10 w-10" />
          ) : (
            <Sparkles className="h-10 w-10" />
          )
        }
        headline={headline}
        body={suspended ? TENANT_SUSPENDED_BODY : TENANT_EMPTY_BODY}
        padding="roomy"
        className={className}
        cta={cta}
      />
    </div>
  );
}

export default TenantStateNotice;
